import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, BookOpen } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ARTICLES, ARTICLES_BY_SLUG } from '../data/articles.jsx';
import SEO from '../seo/SEO';
import { articleSchema, breadcrumbSchema } from '../seo/schemas';
import { SITE_URL } from '../seo/siteConfig';

function renderBlock(block, idx) {
  switch (block.type) {
    case 'p':
      return (
        <p key={idx} className="text-lg text-slate-700 leading-relaxed mb-6">
          {block.children || block.text}
        </p>
      );
    case 'h2':
      return (
        <h2 key={idx} className="mt-12 mb-5 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
          {block.text}
        </h2>
      );
    case 'h3':
      return (
        <h3 key={idx} className="mt-8 mb-4 text-xl font-extrabold text-slate-900">
          {block.text}
        </h3>
      );
    case 'ul':
      return (
        <ul key={idx} className="mb-6 space-y-2.5 text-lg text-slate-700 leading-relaxed pl-5 list-disc marker:text-indigo-400">
          {block.items.map((it, i) => (
            <li key={i} className="pl-1">{it}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={idx} className="mb-6 space-y-2.5 text-lg text-slate-700 leading-relaxed pl-5 list-decimal marker:text-indigo-400">
          {block.items.map((it, i) => (
            <li key={i} className="pl-1">{it}</li>
          ))}
        </ol>
      );
    case 'cta':
      return (
        <div
          key={idx}
          className="my-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 sm:p-7 rounded-2xl bg-indigo-50/70 border border-indigo-200"
        >
          <p className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
            {block.heading}
          </p>
          <Link
            to={block.href}
            className="flex-shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm hover:shadow-md transition-all whitespace-nowrap"
          >
            {block.label}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    case 'quote':
      return (
        <blockquote key={idx} className="my-10 pl-5 border-l-4 border-indigo-500 italic text-xl text-slate-800">
          {block.text}
        </blockquote>
      );
    default:
      return null;
  }
}

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES_BY_SLUG[slug];

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  // Suggest 2 related articles (same category first, then any others)
  const related = ARTICLES.filter((a) => a.slug !== article.slug)
    .sort((a, b) => (a.category === article.category ? -1 : 1))
    .slice(0, 2);

  // Convert "Feb 7, 2026" -> ISO 8601 date for schema.org datePublished.
  const isoDate = (() => {
    try {
      return new Date(article.date).toISOString().split('T')[0];
    } catch {
      return undefined;
    }
  })();

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={`${article.category}, 3PL, ecommerce fulfillment, Shopify fulfillment, ${article.title.toLowerCase()}`}
        schemas={[
          articleSchema({
            slug: article.slug,
            title: article.title,
            excerpt: article.excerpt,
            datePublished: isoDate,
            category: article.category,
          }),
          breadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Resources', url: `${SITE_URL}/blog` },
            { name: article.title, url: `${SITE_URL}/blog/${article.slug}` },
          ]),
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-12 sm:pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 -z-10 brand-soft-bg" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All resources
          </Link>
          <div className="mt-7 flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white border border-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-widest">
              {article.category}
            </span>
            <span className="text-slate-500 font-medium inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> {article.date}
            </span>
            <span className="text-slate-500 font-medium inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {article.readTime}
            </span>
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
            {article.title}
          </h1>
          <p className="mt-5 text-xl text-slate-600 leading-relaxed">{article.excerpt}</p>
        </div>
      </section>

      {/* Body */}
      <article className="pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {article.blocks.map((b, i) => renderBlock(b, i))}
        </div>
      </article>

      {/* End-of-article CTA */}
      <section className="pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-indigo-50 border border-indigo-100 rounded-3xl p-10 text-center">
            <div
              className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-indigo-200/40 blur-3xl pointer-events-none"
              aria-hidden="true"
            />
            <div className="relative">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                Want to see what your store would actually save?
              </h2>
              <p className="mt-3 text-slate-600 max-w-xl mx-auto">
                Get a free, no-pressure estimate in 30 seconds. No call required.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  asChild
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-12 px-7 text-base font-semibold shadow-md hover:shadow-lg group"
                >
                  <Link to="/zones#instant-estimate" className="inline-flex items-center gap-2">
                    Estimate My Savings
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full h-12 px-7 border-slate-300 text-slate-800 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                >
                  <Link to="/#pricing">View Pricing</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="pb-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-2xl font-extrabold text-slate-900">Keep reading</h3>
              <Link to="/blog" className="inline-flex items-center gap-1 text-indigo-600 font-semibold hover:underline">
                <BookOpen className="w-4 h-4" /> All resources
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  className="group block bg-white border border-slate-200 rounded-2xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all duration-200"
                >
                  <div className="flex items-center gap-2 mb-3 text-xs text-slate-500">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-bold uppercase tracking-widest">
                      {a.category}
                    </span>
                    <span>{a.readTime}</span>
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-indigo-700 transition-colors">
                    {a.title}
                  </h4>
                  <p className="mt-2 text-slate-600 leading-relaxed text-sm">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-indigo-600 font-semibold text-sm group-hover:gap-2.5 transition-all">
                    Read article <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
